/* eslint-disable testing-library/no-debugging-utils */
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { Logger } from '@egw/logger'
import { isValidConfig } from '@egw/config'
import { type Config, configSchema } from '../../config/config'

declare global {
  interface Window {
    // eslint-disable-next-line no-unused-vars
    zE?: (...args: unknown[]) => void
  }
}

export interface ConversationField {
  id: string
  value: number | string | boolean
}

interface CustomerDetails {
  customer: {
    email: string
  }
  customerService: {
    tokens: {
      helpCenterToken: string
      inAppChatToken: string
    }
  }
}

const log = new Logger('InApp Chat Service - Zendesk')

let instance: InAppChatService | null = null

class InAppChatService {
  private isInitialized = false
  private isInAppChatOpen = false
  private email: string | undefined
  private jobQueue: (() => void)[] = []

  constructor() {
    if (!instance) {
      instance = this

      // Notificaitons listeners - on authenticated
      const userLoadedListener = (data: unknown): void => {
        const eventData = data as { detail: CustomerDetails }
        if (this.email !== eventData.detail.customer.email) {
          log.debug('logging user into chat widget...')
          this.enqueue(() => {
            window.zE?.('messenger', 'loginUser', (cb: (_jwt: string) => void) => {
              cb(eventData.detail.customerService.tokens.inAppChatToken)
            })
          })
          this.email = eventData.detail.customer.email
        }
      }
      notifications.on(NOTIFICATIONS.API.CUSTOMER_DETAILS, userLoadedListener)

      // Notificaitons listeners - on sign out
      const signOutListener = (): void => {
        log.debug('logging user out of chat widget...')
        this.enqueue(() => {
          window.zE?.('messenger', 'logoutUser')
        })
        this.email = undefined
      }
      notifications.on(NOTIFICATIONS.AUTH.SIGN_OUT, signOutListener)
    }
  }

  private enqueue(action: () => void): void {
    if (this.isInitialized && typeof window !== 'undefined' && window.zE) {
      action()
    } else {
      log.debug('Queuing job as service is not initialized')
      this.jobQueue.push(action)
    }
  }

  private flushQueue(): void {
    while (this.jobQueue.length > 0) {
      const action = this.jobQueue.shift()
      action?.()
    }
  }

  init = (config: Config): void => {
    if (!isValidConfig(config, configSchema)) {
      log.error('Zendesk configuration is invalid')
      throw new Error('Zendesk configuration is invalid')
    }
    if (!this.isInitialized) {
      if (typeof window === 'undefined') return

      // Event handlers
      const onUnreadMessages = (unreadMessageCount: number): void => {
        if (unreadMessageCount > 0) {
          log.debug(`You have ${unreadMessageCount.toString()} unread message(s).`)
        }
      }
      const onOpen = (): void => {
        log.debug('In App Chat opened')
        this.isInAppChatOpen = true
        notifications.dispatch(NOTIFICATIONS.IN_APP_CHAT.OPEN)
      }
      const onClose = (): void => {
        log.debug('In App Chat closed')
        this.isInAppChatOpen = false
        notifications.dispatch(NOTIFICATIONS.IN_APP_CHAT.CLOSE)
      }

      const documentBody = window.document.getElementsByTagName('body')[0]
      const chatScript = window.document.createElement('script')
      chatScript.defer = true
      chatScript.id = 'ze-snippet'
      chatScript.src = `https://static.zdassets.com/ekr/snippet.js?key=${config.zendesk.inAppChatKey}`
      chatScript.addEventListener('load', () => {
        if (window.zE) {
          window.zE('messenger:on', 'open', onOpen)
          window.zE('messenger:on', 'close', onClose)
          window.zE('messenger:on', 'unreadMessages', onUnreadMessages)
          this.isInitialized = true
          log.info('In App Chat is initialized')
          this.flushQueue()
        }
      })
      documentBody.appendChild(chatScript)
    } else {
      log.warn('Zendesk in app chat has already been initialized. Skipping initialization...')
    }
  }

  open = (): void => {
    if (this.isInitialized) {
      if (!this.isInAppChatOpen) {
        log.debug('opening chat widget...')
        typeof window !== 'undefined' && window.zE && window.zE('messenger', 'open')
      }
    } else {
      log.warn('In App Chat is not initialized. Skipping...')
    }
  }

  close = (): void => {
    log.debug('closing chat widget...')
    typeof window !== 'undefined' && window.zE && window.zE('messenger', 'close')
  }

  setTags = (tags: string[]): void => {
    log.debug('setting tags in chat...')
    this.enqueue(() => {
      window.zE?.('messenger:set', 'conversationTags', tags)
    })
  }

  setFields = (fields: ConversationField[]): void => {
    log.debug('setting tags in chat...')
    this.enqueue(() => {
      window.zE?.('messenger:set', 'conversationFields', fields)
    })
  }

  getIsInAppChatOpen = (): boolean => {
    return this.isInAppChatOpen
  }
}

export const inAppChatService = new InAppChatService()
